import { useEffect, useRef, useState } from 'react';

import { type BirdFrames, birdFrames } from './agents-bird.frames';
import { blendRadii, drawHalftone, waveRadii } from './agents-bird.halftone';
import { type BirdActivity } from './agents-bird.look';
import {
  type DotScene,
  finishScene,
  hatchScene,
  morphScene,
} from './agents-bird.moments';
import {
  BIRD_MOTION,
  type BirdMotion,
  type BirdSpecies,
  WING_BEATS,
} from './agents-bird.species';
import {
  type DotSet,
  drawDots,
  THINKING_EXIT_MS,
  stillThinkingDots,
  thinkingScene,
  visibleDots,
  waveDots,
} from './agents-bird.thinking';

const WING_BEAT_MS = 110;
const RAISE_EVERY_MS = 2600;
const RAISE_STEP_MS = 90;
const RAISE_SEQUENCE = [0, 8, 15, 15, 15, 15, 15, 8, 0];
const RAISE_MS = RAISE_STEP_MS * (RAISE_SEQUENCE.length - 1);
const RAISE_HELD_MS = RAISE_STEP_MS * 3;
const HOVER_RIPPLE_MS = 1100;
const HOVER_RIPPLE_REACH = 1.1;
const HOVER_RIPPLE_STRENGTH = 0.6;
const HOVER_RIPPLE_THICKNESS = 2.2;
const HOVER_FLICK_MS = 700;
const HOVER_FLICK = [0, 10, 18, 10, 0, -6, 0];
const FLICK_SIZE = 30;
const REST_AFTER_MS = 120_000;
const REST_IN_MS = 900;
const WAKE_MS = 320;
const REST_WING = -8;
const REST_ALPHA = 0.72;
const SPECIES_MORPH_MS = 700;
const FRAME_INTERVAL_MS = 33;
const ERROR_ALPHA = 0.5;

const ANIMATED_ACTIVITIES = new Set<BirdActivity>([
  'thinking',
  'working',
  'waiting',
]);

type HoverRipple = { origin: { x: number; y: number }; progress: number };

type Loop = (frame: (elapsed: number) => boolean) => () => void;

type Visibility = {
  isVisible: () => boolean;
  subscribe: (listener: () => void) => () => void;
};

type BirdPainter = (
  activity: BirdActivity,
  elapsed: number,
  hover: HoverRipple | null,
  resting?: number
) => void;

type ShownBird = {
  key: string;
  species: BirdSpecies;
  activity: BirdActivity;
  dots: DotSet | null;
};

type WingPose = { from: number; to: number; amount: number };

const poseAlong = (sequence: number[], step: number): WingPose => {
  const index = Math.floor(step);
  return {
    from: sequence[index % sequence.length],
    to: sequence[(index + 1) % sequence.length],
    amount: step - index,
  };
};

const wingPoseAt = (
  activity: BirdActivity,
  elapsed: number
): WingPose | null => {
  if (activity === 'working') {
    return poseAlong(WING_BEATS, elapsed / WING_BEAT_MS);
  }
  if (activity !== 'waiting') return null;
  const step = (elapsed % RAISE_EVERY_MS) / RAISE_STEP_MS;
  return step < RAISE_SEQUENCE.length - 1
    ? poseAlong(RAISE_SEQUENCE, step)
    : null;
};

const clipToMask = (
  context: CanvasRenderingContext2D,
  mask: HTMLCanvasElement
) => {
  context.globalCompositeOperation = 'destination-in';
  context.drawImage(mask, 0, 0);
  context.globalCompositeOperation = 'source-over';
};

const birdPainter = (
  context: CanvasRenderingContext2D,
  frames: BirdFrames,
  color: string,
  motion: BirdMotion,
  flick: boolean
): BirdPainter => {
  const rest = frames.radii();
  const animated = new Float32Array(rest.length);
  const rippleReach = context.canvas.width * HOVER_RIPPLE_REACH;
  return (activity, elapsed, hover, resting = 0) => {
    const pose =
      flick && hover
        ? poseAlong(HOVER_FLICK, hover.progress * (HOVER_FLICK.length - 1))
        : wingPoseAt(activity, elapsed * motion.tempo);
    let radii = rest;
    let wingBeat = 0;
    if (pose) {
      radii = blendRadii(
        frames.radii(pose.from * motion.swing),
        frames.radii(pose.to * motion.swing),
        pose.amount,
        animated
      );
      wingBeat = (pose.amount < 0.5 ? pose.from : pose.to) * motion.swing;
    } else if (resting > 0) {
      radii = blendRadii(rest, frames.radii(REST_WING), resting, animated);
      wingBeat = resting < 0.5 ? 0 : REST_WING;
    }
    if (hover && !flick) {
      radii = waveRadii(
        radii,
        frames.grid,
        hover.origin,
        hover.progress * rippleReach,
        HOVER_RIPPLE_STRENGTH * (1 - hover.progress),
        animated
      );
    }
    drawHalftone(context, frames.grid, radii, {
      color,
      alpha:
        activity === 'error' ? ERROR_ALPHA : 1 - (1 - REST_ALPHA) * resting,
    });
    if (frames.mask) clipToMask(context, frames.mask(wingBeat));
  };
};

const startLoop = (
  frame: (elapsed: number) => boolean,
  visibility: Visibility
) => {
  const startedAt = performance.now();
  let lastFrameAt = 0;
  let request = 0;
  let unsubscribe = () => {};
  const stop = () => {
    cancelAnimationFrame(request);
    request = 0;
    unsubscribe();
  };
  const tick = (now: number) => {
    request = 0;
    if (!visibility.isVisible()) return;
    request = requestAnimationFrame(tick);
    if (now - lastFrameAt < FRAME_INTERVAL_MS) return;
    lastFrameAt = now;
    if (!frame(Math.max(0, now - startedAt))) stop();
  };
  unsubscribe = visibility.subscribe(() => {
    if (request === 0 && visibility.isVisible()) {
      request = requestAnimationFrame(tick);
    }
  });
  request = requestAnimationFrame(tick);
  return stop;
};

const liveIdle = (
  paint: BirdPainter,
  currentRipple: () => HoverRipple | null,
  loop: Loop
) => {
  let resting = 0;
  let restTimer = 0;
  let stopLoop = () => {};
  const scheduleRest = () => {
    window.clearTimeout(restTimer);
    restTimer = window.setTimeout(() => {
      const from = resting;
      stopLoop();
      stopLoop = loop((elapsed) => {
        resting = Math.min(1, from + elapsed / REST_IN_MS);
        paint('idle', 0, null, resting);
        return resting < 1;
      });
    }, REST_AFTER_MS);
  };
  const onHover = () => {
    const wakeFrom = resting;
    stopLoop();
    stopLoop = loop((elapsed) => {
      resting = Math.max(0, wakeFrom * (1 - elapsed / WAKE_MS));
      const ripple = currentRipple();
      paint('idle', 0, ripple, resting);
      return ripple !== null || resting > 0;
    });
    scheduleRest();
  };
  scheduleRest();
  return {
    onHover,
    stop: () => {
      stopLoop();
      window.clearTimeout(restTimer);
    },
  };
};

const liveWaiting = (
  paint: BirdPainter,
  currentRipple: () => HoverRipple | null,
  loop: Loop,
  tempo: number
) => {
  const startedAt = performance.now();
  let running = false;
  let timer = 0;
  let stopLoop = () => {};
  const raise = () => {
    window.clearTimeout(timer);
    if (running) return;
    running = true;
    stopLoop = loop(() => {
      const elapsed = performance.now() - startedAt;
      const ripple = currentRipple();
      paint('waiting', elapsed, ripple);
      const phase = (elapsed * tempo) % RAISE_EVERY_MS;
      if (phase < RAISE_MS || ripple) return true;
      running = false;
      timer = window.setTimeout(raise, (RAISE_EVERY_MS - phase) / tempo);
      return false;
    });
  };
  raise();
  return {
    onHover: raise,
    stop: () => {
      stopLoop();
      window.clearTimeout(timer);
    },
  };
};

type BirdCanvasOptions = {
  species: BirdSpecies;
  color: string;
  activity: BirdActivity;
  dark: boolean;
  hatch: boolean;
};

export const useBirdCanvas = ({
  species,
  color,
  activity,
  dark,
  hatch,
}: BirdCanvasOptions) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hostRef = useRef<HTMLSpanElement>(null);
  const shownRef = useRef<ShownBird | null>(null);
  const hatchedRef = useRef(false);
  const visibleRef = useRef(true);
  const visibilityListenersRef = useRef(new Set<() => void>());
  const [cssSize, setCssSize] = useState(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const observer = new ResizeObserver(([entry]) =>
      setCssSize(Math.round(entry.contentRect.width))
    );
    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const observer = new IntersectionObserver(([entry]) => {
      visibleRef.current = entry.isIntersecting;
      for (const listener of visibilityListenersRef.current) listener();
    });
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const host = hostRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !host || !context || cssSize === 0) return;
    const pixels = Math.round(cssSize * window.devicePixelRatio);
    if (canvas.width !== pixels) {
      canvas.width = pixels;
      canvas.height = pixels;
    }

    const visibility: Visibility = {
      isVisible: () => visibleRef.current,
      subscribe: (listener) => {
        const listeners = visibilityListenersRef.current;
        listeners.add(listener);
        return () => listeners.delete(listener);
      },
    };
    const loop: Loop = (frame) => startLoop(frame, visibility);
    const frames = birdFrames(species, cssSize, pixels, dark);
    const motion = BIRD_MOTION[species];
    const flick = cssSize <= FLICK_SIZE;
    const paint = birdPainter(context, frames, color, motion, flick);
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const bird = visibleDots(frames.grid, frames.radii());
    const key = `${pixels}:${dark}`;
    const previous = shownRef.current?.key === key ? shownRef.current : null;
    const shown: ShownBird = { key, species, activity, dots: null };
    shownRef.current = shown;

    const scenes: DotScene[] = [];
    const shouldHatch = hatch && !hatchedRef.current;
    if (hatch) hatchedRef.current = true;
    if (!still && shouldHatch) {
      scenes.push(hatchScene(bird, pixels, frames.grid.pitch, dark));
    } else if (!still && previous?.dots) {
      if (activity !== 'thinking' || previous.species !== species) {
        scenes.push(morphScene(previous.dots, bird, THINKING_EXIT_MS));
      }
    } else if (!still && previous && previous.species !== species) {
      const from = birdFrames(previous.species, cssSize, pixels, dark);
      scenes.push(
        morphScene(visibleDots(from.grid, from.radii()), bird, SPECIES_MORPH_MS)
      );
    }
    const finished =
      previous?.activity === 'thinking' &&
      (activity === 'waiting' || activity === 'idle');
    if (!still && finished) scenes.push(finishScene(bird));

    const rippleMs =
      (flick ? HOVER_FLICK_MS : HOVER_RIPPLE_MS) / Math.sqrt(motion.tempo);
    let hover: { origin: { x: number; y: number }; startedAt: number } | null =
      null;
    const currentRipple = (): HoverRipple | null => {
      if (!hover) return null;
      const progress = (performance.now() - hover.startedAt) / rippleMs;
      return progress < 1 ? { origin: hover.origin, progress } : null;
    };
    const rippleDots = (dots: DotSet) => {
      const ripple = flick ? null : currentRipple();
      return ripple
        ? waveDots(
            dots,
            ripple.origin,
            ripple.progress * pixels * HOVER_RIPPLE_REACH,
            HOVER_RIPPLE_STRENGTH * (1 - ripple.progress),
            frames.grid.pitch * HOVER_RIPPLE_THICKNESS
          )
        : dots;
    };

    let stop = () => {};
    let onHover = () => {};
    const settle = () => {
      shown.dots = null;
      if (still) {
        if (activity === 'thinking' || activity === 'working') {
          drawDots(
            context,
            stillThinkingDots(pixels, frames.grid.pitch, dark),
            color
          );
        } else if (activity === 'waiting') {
          paint('waiting', RAISE_HELD_MS / motion.tempo, null);
        } else {
          paint(activity === 'error' ? 'error' : 'idle', 0, null);
        }
        return;
      }
      if (!ANIMATED_ACTIVITIES.has(activity)) {
        const resting = activity === 'error' ? 'error' : 'idle';
        paint(resting, 0, null);
        if (resting === 'idle') {
          const idle = liveIdle(paint, currentRipple, loop);
          stop = idle.stop;
          onHover = idle.onHover;
          return;
        }
        onHover = () => {
          stop();
          stop = loop(() => {
            const ripple = currentRipple();
            paint('error', 0, ripple);
            return ripple !== null;
          });
        };
        return;
      }
      if (activity === 'thinking') {
        const scene = thinkingScene(bird, pixels, frames.grid.pitch, dark);
        const pace = Math.sqrt(motion.tempo);
        stop = loop((elapsed) => {
          const { dots, isBird } = scene(elapsed * pace);
          drawDots(context, rippleDots(dots), color);
          if (isBird && frames.mask) clipToMask(context, frames.mask(0));
          shown.dots = isBird ? null : dots;
          return true;
        });
        return;
      }
      if (activity === 'waiting') {
        const waiting = liveWaiting(paint, currentRipple, loop, motion.tempo);
        stop = waiting.stop;
        onHover = waiting.onHover;
        return;
      }
      stop = loop((elapsed) => {
        paint(activity, elapsed, currentRipple());
        return true;
      });
    };

    const play = (index: number) => {
      const scene = scenes[index];
      if (!scene) {
        settle();
        return;
      }
      stop = loop((elapsed) => {
        const dots = scene(elapsed);
        if (!dots) {
          play(index + 1);
          return false;
        }
        drawDots(context, rippleDots(dots), color);
        shown.dots = dots;
        return true;
      });
    };

    const onPointerEnter = (event: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      hover = {
        origin: {
          x: ((event.clientX - rect.left) / rect.width) * pixels,
          y: ((event.clientY - rect.top) / rect.height) * pixels,
        },
        startedAt: performance.now(),
      };
      onHover();
    };
    if (!still) host.addEventListener('pointerenter', onPointerEnter);
    play(0);
    return () => {
      stop();
      host.removeEventListener('pointerenter', onPointerEnter);
    };
  }, [cssSize, species, color, activity, dark, hatch]);

  return { canvasRef, hostRef };
};
